import type { CashierJwtPayload } from '@matix/types';
import { useCart } from '../store/cart';

/**
 * Who to name on a receipt this till is printing right now.
 *
 * The slip says "Cashier: …", and until 2026-09-15 that was `username` — the
 * LOGIN name, `Security_Users.LoginName`. A customer querying their bill was
 * handed "afathy" rather than the person they had just spoken to.
 *
 * The real names ride on the session JWT (`display_name` / `display_name_ar`,
 * from `Security_Users.Name` / `.Name_AR`), so there is nothing to fetch. Both
 * are optional: a token minted before those claims existed carries neither,
 * and this falls back to the login name exactly as before rather than printing
 * a blank line. Nobody is logged out by the change — they see the login name
 * until their next login.
 *
 * Which of the two prints is the RECEIPT's decision, not this one: it has both
 * and picks by the slip's language (see `cashierNameOf` in @matix/receipt).
 */
export function cashierIdentity(cashier: CashierJwtPayload): {
  id: number;
  name: string | null;
  nameAr: string | null;
} {
  // "Change Salesman": the receipt is credited to someone else, and that is
  // who the slip names — as the legacy till printed it (Staff/اسم البائع).
  // Every caller prints BEFORE the cart is reset, so the choice is still here.
  const creator = useCart.getState().creator;
  if (creator && creator.id !== cashier.sub) {
    return { id: creator.id, name: creator.name, nameAr: creator.nameAr };
  }
  const en = (cashier.display_name ?? '').trim();
  const ar = (cashier.display_name_ar ?? '').trim();
  return {
    id: cashier.sub,
    name: en || cashier.username || null,
    nameAr: ar || null,
  };
}
